import { forwardRef, type ReactNode } from "react";
import { X } from "lucide-react";

import { RIGHT_RAIL_WIDTH } from "./rightRailConstants";

interface RightRailPanelProps {
  /** Hidden panels stay mounted (iframes keep their session); only visibility toggles. */
  visible: boolean;
  icon: ReactNode;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Frosted-glass floating panel that opens next to the right app rail
 * (Chat / Notes / Support / Zaviago AI). One shell so every rail panel looks and
 * behaves the same.
 */
export const RightRailPanel = forwardRef<HTMLDivElement, RightRailPanelProps>(
  function RightRailPanel({ visible, icon, title, onClose, children }, ref) {
    return (
      <div
        ref={ref}
        className="fixed z-40 flex flex-col overflow-hidden rounded-xl"
        style={{
          width: 420,
          top: 12,
          right: RIGHT_RAIL_WIDTH + 8,
          bottom: 12,
          background: "rgb(255 255 255 / 35%)",
          backdropFilter: "blur(40px) saturate(200%)",
          WebkitBackdropFilter: "blur(40px) saturate(200%)",
          border: "1px solid rgb(255 255 255 / 30%)",
          boxShadow: "0 8px 32px rgb(0 0 0 / 8%), inset 0 0 0 0.5px rgb(255 255 255 / 25%)",
          visibility: visible ? "visible" : "hidden",
          pointerEvents: visible ? "auto" : "none",
        }}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 flex items-center justify-center">{icon}</div>
            <span className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 rounded-full bg-white/40 dark:bg-white/10 text-gray-500 hover:bg-white/60 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {children}
      </div>
    );
  }
);
